import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./modal.css";

export interface ModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  variant?: "default" | "destructive";
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** When set, the Confirm button is disabled until the user types this exact string. */
  requireTypedConfirmation?: string;
  /**
   * Suppress the confirm button entirely. Use for picker-style modals where
   * the selection inside `children` is itself the confirmation and a confirm
   * button would be a redundant tap.
   */
  hideConfirm?: boolean;
  /** Child slot for typed confirm input or extra content. */
  children?: ReactNode;
}

export function Modal({
  open,
  title,
  description,
  variant = "default",
  confirmLabel = "Підтвердити",
  cancelLabel = "Скасувати",
  onConfirm,
  onCancel,
  hideConfirm = false,
  children,
}: ModalProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    titleRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className="wp-modal-overlay" role="presentation" onClick={onCancel}>
      <div
        className={`wp-modal wp-modal--${variant}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="wp-modal-title" ref={titleRef} tabIndex={-1} className="wp-modal__title">
          {title}
        </h2>
        {description ? <div className="wp-modal__desc">{description}</div> : null}
        {children}
        <div className="wp-modal__actions">
          <button type="button" className="wp-btn wp-btn--secondary wp-btn--md" onClick={onCancel}>
            {cancelLabel}
          </button>
          {hideConfirm ? null : (
            <button
              type="button"
              className={`wp-btn wp-btn--${variant === "destructive" ? "danger" : "primary"} wp-btn--md`}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
